import { useState } from "react";
import { flushSync } from "react-dom";
import urlUtils from "@/common/utils/urlUtils";
import { eventNames, valueNames } from "@/modules/Constants";
import * as watchTimeTracker from "@/modules/WatchTimeTracker";
import * as analytics from "@/modules/analytics";
import appController from "@/modules/appController";
import { prefetchChain } from "@/stores/chain/actions";
import type { TimelineMarker } from "@/common/videojs/timelineMarkerTypes";
import {
  REGION,
  type VideoJsSelf,
  type VideoPlayer,
} from "@/components/player/videoJsTypes";

// Video.js event handlers that report playback (analytics, app events, media ready).
function createVideoTracking(self: VideoJsSelf) {
  // Reads the player at call time; throws on a null player as the class did.
  const playerNow = () => self.player as VideoPlayer;
  const currentTime = () => playerNow().currentTime() as number;

  function parentMediaWarmHint() {
    const qs = urlUtils.querySettings();
    return qs["pix-media-warm"] === "1" || qs["pix-media-warm"] === "true";
  }

  function reportMediaReady() {
    const experienceData = self.props.experienceState.experienceData;
    if (!self.player || !experienceData.isLoaded()) {
      return;
    }
    if (experienceData.idexperience === undefined) {
      return;
    }
    if (self.mediaReadyExperienceId === experienceData.idexperience) {
      return;
    }
    // Publisher warmed media: poster + metadata is enough to drop overlay spinner.
    const minReadyState = parentMediaWarmHint() ? 1 : 2;
    if (self.player.readyState?.() < minReadyState) {
      return;
    }
    self.mediaReadyExperienceId = experienceData.idexperience;
    appController.reportMediaReady();
  }

  function onPlayerMediaReady() {
    reportMediaReady();
    // Fallback prefetch (deduped) in case loadstart was missed — see
    // onPlayerLoadStart for the primary, earlier trigger.
    prefetchChainForCurrent();
  }

  // Prefetch the next-up chain as EARLY as possible — at loadstart (media
  // loading has begun), not media-ready. The chain request only needs the
  // experience id, so firing it here runs the backend round-trip in parallel
  // with video buffering instead of after `canplay`; the chain is then usually
  // ready by the time playback starts, so the next arrow is live from the start.
  // Deduped per experience in prefetchChain, so the media-ready fallback is a
  // no-op once this has fired. This is a plain video event (no store dispatch),
  // so prefetchChain's own dispatch is safe here.
  function onPlayerLoadStart() {
    prefetchChainForCurrent();
  }

  function prefetchChainForCurrent() {
    const experienceData = self.props.experienceState.experienceData;
    if (experienceData) {
      prefetchChain(experienceData.idexperience);
    }
  }

  function onPlayerPlay() {
    const experienceData = self.props.experienceState.experienceData;
    if (!experienceData.isLoaded()) {
      self.pendingUserPlay = true;
      if (self.player) {
        self.player.pause();
      }
      return;
    }
    if (self.props.onApplicationEvent) {
      self.props.onApplicationEvent({
        name: eventNames.PLAY,
        region: REGION,
      });
    }
    analytics.track("Play", {
      pix_offset: self.props.experienceState.experienceData.nowTime,
    });
  }

  function onPlayerPlaying() {
    reportMediaReady();
    playerNow().off("timeupdate", onTimeUpdate); // no dups
    playerNow().on("timeupdate", onTimeUpdate);
    watchTimeTracker.start();
    // Publish playback state so the chain countdown only advances while
    // playing — see valueNames.VIDEO_PLAYING.
    self.props.experienceState.applicationData.setValue(
      valueNames.VIDEO_PLAYING,
      true,
    );
  }

  function onPlayerPause() {
    if (!self.props.experienceState.experienceData.isLoaded()) {
      return;
    }
    // Pause freezes the chain countdown. The natural end also pauses the
    // player, but there VIDEO_ENDED is set and the countdown must still run to
    // auto-advance, so the consumer treats `ended` as overriding this flag.
    self.props.experienceState.applicationData.setValue(
      valueNames.VIDEO_PLAYING,
      false,
    );
    watchTimeTracker.pause();
    if (playerNow().scrubbing() === false) {
      analytics.track("Pause", {
        pix_offset: self.props.experienceState.experienceData.nowTime,
      });
    }
    // Fallback for unreliable 'ended' event: fire Video Ended here so
    // watchTimeTracker.getSeconds() includes the full watch time up to this pause.
    if (self.videoEndedPending && !self.videoEndedFired) {
      self.videoEndedPending = false;
      self.videoEndedFired = true;
      analytics.track("Video Ended", {
        pix_offset: self.props.experienceState.experienceData.nowTime,
        pix_watch_time: watchTimeTracker.getSeconds(),
      });
      if (self.props.onApplicationEvent) {
        self.props.onApplicationEvent({
          name: eventNames.VIDEO_ENDED,
          timelineName: self.props.timelineName,
        });
      }
    }
  }

  function onPlayerEnded() {
    if (!self.props.experienceState.experienceData.isLoaded()) {
      return;
    }
    playerNow().off("timeupdate", onTimeUpdate);
    if (self.videoEndedFired) {
      return;
    }
    self.videoEndedFired = true;
    analytics.track("Video Ended", {
      pix_offset: self.props.experienceState.experienceData.nowTime,
      pix_watch_time: watchTimeTracker.getSeconds(),
    });
    if (self.props.onApplicationEvent) {
      self.props.onApplicationEvent({
        name: eventNames.VIDEO_ENDED,
        timelineName: self.props.timelineName,
      });
    }
  }

  function onTimeUpdate() {
    if (!self.props.experienceState.experienceData.isLoaded()) {
      return;
    }
    const time = Math.floor(currentTime() * 10.0) / 10.0;
    if (time < self.articleTime || time > self.articleTime) {
      self.articleTime = time;
      const onApplicationEvent = self.props.onApplicationEvent;
      if (onApplicationEvent) {
        // Render the sync now, so the chain reveal events come before Video Tick.
        flushSync(() =>
          onApplicationEvent({
            name: eventNames.TIME_SYNC,
            timelineName: self.props.timelineName,
            time: time,
            // Carry duration so the store can derive remaining time for the
            // time-based chain reveal/countdown.
            duration: playerNow().duration(),
          }),
        );
      }
    }

    // A video can be corrupt from a transcoder error and have a duration
    // of 0.
    if ((playerNow().duration() as number) > 0.0) {
      const percent =
        Math.floor(
          (currentTime() / (playerNow().duration() as number)) * 10 + 0.5,
        ) * 10;
      if (percent > 0.0 && percent <= 100 && percent > self.percentComplete) {
        self.percentComplete = percent;
        analytics.track("Video Tick", {
          pix_offset: self.props.experienceState.experienceData.nowTime,
          pix_percent: self.percentComplete,
        });
        // Signal that the video has reached its end; actual Video Ended event
        // fires in onPlayerPause so watchTimeTracker captures the full watch
        // time including the last few seconds after the 100% tick.
        if (percent === 100) {
          self.videoEndedPending = true;
        }
      }
    }
  }

  function onPlayerSeeking() {
    if (!self.props.experienceState.experienceData.isLoaded()) {
      return;
    }
    if (!self.isSeeking) {
      self.seekStart = currentTime();
      self.isSeeking = true;
    }
  }

  function onPlayerSeeked() {
    if (!self.props.experienceState.experienceData.isLoaded()) {
      return;
    }
    self.isSeeking = false;
    self.videoEndedFired = false;
    self.videoEndedPending = false;
    const start = Math.floor(self.seekStart);
    const end = Math.floor(currentTime());
    if (Math.abs(start - end) >= 1) {
      analytics.track("Time-Shift", {
        pix_offset_from: start,
        pix_offset_to: end,
      });
    }

    if (self.props.onApplicationEvent) {
      self.props.onApplicationEvent({
        name: eventNames.VIDEO_POSITION,
        value: -1,
      });
    }
  }

  function onPlayerError() {
    if (!self.props.experienceState.experienceData.isLoaded()) {
      return;
    }
    const error = playerNow().error();
    const extra: Record<string, unknown> = {};
    if (error) {
      const ctor = error.constructor as { errorTypes?: string[] } | undefined;
      extra.pix_error_code = error.code;
      if (ctor && ctor.errorTypes && ctor.errorTypes.length > error.code) {
        extra.pix_error = ctor.errorTypes[error.code];
      } else {
        extra.pix_error = "ERR_NO_ERROR_MSG_FOUND";
      }
    }
    analytics.track("Video Error", extra);
  }

  function onPlayerStalled() {
    if (!self.props.experienceState.experienceData.isLoaded()) {
      return;
    }
    analytics.track("Video Stalled", {
      pix_offset: self.props.experienceState.experienceData.nowTime,
    });
  }

  function onMarkerMouseDown(marker: TimelineMarker) {
    self.props.onApplicationEvent({
      name: eventNames.VIDEO_MARKER_SELECTED,
      region: REGION,
      timelineName: self.props.timelineName,
      marker: marker,
    });
  }

  function onMarkerReached(marker: TimelineMarker) {
    self.props.onApplicationEvent({
      name: eventNames.VIDEO_MARKER_REACHED,
      region: REGION,
      timelineName: self.props.timelineName,
      marker: marker,
    });
  }

  function onResolutionChanged() {
    if (!self.props.experienceState.experienceData.isLoaded()) {
      return;
    }
    // With no label, currentResolution() returns the current state.
    const resolution = playerNow().currentResolution();
    analytics.track("Resolution Changed", {
      pix_offset: self.props.experienceState.experienceData.nowTime,
      pix_resolution: resolution.label,
    });
  }

  return {
    reportMediaReady,
    onPlayerMediaReady,
    onPlayerLoadStart,
    onPlayerPlay,
    onPlayerPlaying,
    onPlayerPause,
    onPlayerEnded,
    onTimeUpdate,
    onPlayerSeeking,
    onPlayerSeeked,
    onPlayerError,
    onPlayerStalled,
    onMarkerMouseDown,
    onMarkerReached,
    onResolutionChanged,
  };
}

export type VideoTracking = ReturnType<typeof createVideoTracking>;

/** The playback handlers of one VideoJs mount; stable for the mount, so off() matches on(). */
export function useVideoTracking(self: VideoJsSelf): VideoTracking {
  const [tracking] = useState(() => createVideoTracking(self));
  return tracking;
}
